import { useState } from 'react';
import { BiCheck, BiChevronDown } from 'react-icons/bi';

import { AnchoredPopover } from '@/components/AnchoredPopover/AnchoredPopover';
import type { PersonsMap } from '@/hooks/usePersonsMap';

import styles from './AssigneeCut.module.scss';

/** `filters.responsible` value meaning "everybody's tasks, mine included". */
export const EVERYONE = '';

/**
 * Whose tasks the list is about — my own, or a chosen set of people.
 *
 * One control, two segments, and the second segment is named by whatever is
 * selected inside it: `[ My tasks | Everyone ⌄ ]` becomes
 * `[ My tasks | Amelia Hartley ⌄ ]`. Both segments therefore name a scope of
 * people, which is what makes them read as parallel choices.
 *
 * The second segment carries both behaviours without a mode to learn, because
 * the two can never apply at once: a segment that is not selected has one job
 * (switch to this scope), and a segment that *is* selected has nothing to do on
 * click, since re-selecting it is a no-op. So one hit target means "switch to
 * this scope" while inactive and "change who" while active. The chevron appears
 * only in the second state, so the affordance is true exactly when it is
 * actionable.
 *
 * The cut writes `filters.responsible`, the same field the server filters on, so
 * this control and the applied-filter state can never disagree.
 */
export const AssigneeCut = ({
  value,
  onChange,
  personsMap,
  currentUserId,
  className,
}: {
  /** Current `filters.responsible`; `EVERYONE` means no cut at all. */
  value: string;
  onChange: (responsible: string) => void;
  personsMap: PersonsMap;
  /**
   * The signed-in user's person id. When it is missing there is no such thing as
   * "my tasks" here, so that segment is dropped rather than shown as a choice
   * that cannot mean anything.
   */
  currentUserId?: string;
  className?: string;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  // State, not a ref: the popover needs the anchor element during render, and a
  // ref read there is a value React never re-renders for.
  const [segmentEl, setSegmentEl] = useState<HTMLButtonElement | null>(null);

  const mineAvailable = !!currentUserId;
  const onPeople = !mineAvailable || value !== currentUserId;
  // Only meaningful on the people segment. While "My tasks" is the selected one
  // the cut belongs to *that* segment, so this one names the scope clicking it
  // lands on — "Everyone" — instead of borrowing your own name and printing the
  // same person on both halves of the control.
  const person = onPeople && value !== EVERYONE ? value : undefined;

  const name = (id: string) => {
    const found = personsMap.get(id);
    return found
      ? `${found.person_info.first_name} ${found.person_info.last_name}`.trim()
      : undefined;
  };

  // A stale selection can name somebody who is not on this account any more —
  // say that, rather than leaving an empty list unexplained. With no person at
  // all this is "Everyone", which is both the label of the unselected segment
  // and the scope clicking it lands on.
  const personLabel = person ? (name(person) ?? 'Not on This Account') : 'Everyone';

  // Yourself is left out of the roster: picking your own name is the cut the
  // "My tasks" segment already makes, so listing it here offers the same scope
  // twice and bounces the control back to the other segment. With no signed-in id
  // there is no "My tasks" segment to stand in for you, so nothing is dropped.
  const options: { value: string; label: string }[] = [
    { value: EVERYONE, label: 'Everyone' },
    ...Array.from(personsMap.values())
      .filter((p) => p.id !== currentUserId)
      .map((p) => ({
        value: p.id,
        label: `${p.person_info.first_name} ${p.person_info.last_name}`.trim(),
      })),
    // Keep an unknown id in the list while it is selected, so the menu shows what
    // is actually applied instead of nothing checked at all.
    ...(person && !name(person) ? [{ value: person, label: personLabel }] : []),
  ];

  const select = (next: string) => {
    setMenuOpen(false);
    if (next !== value) onChange(next);
  };

  return (
    <div className={`${styles.cut} ${className ?? ''}`}>
      <div className={styles.segmented} role="tablist" aria-label="Whose Tasks">
        {mineAvailable && (
          <button
            type="button"
            role="tab"
            aria-selected={!onPeople}
            className={`${styles.option} ${!onPeople ? styles.optionActive : ''}`}
            onClick={() => select(currentUserId)}
          >
            My Tasks
          </button>
        )}

        <button
          ref={setSegmentEl}
          type="button"
          role="tab"
          aria-selected={onPeople}
          // Selected, this element is both the tab and the trigger for the person
          // menu; unselected it is a plain tab that switches scope, so it carries
          // no chevron to promise otherwise.
          aria-haspopup={onPeople ? 'listbox' : undefined}
          aria-expanded={onPeople ? menuOpen : undefined}
          className={`${styles.option} ${styles.people} ${onPeople ? styles.optionActive : ''}`}
          onClick={() => (onPeople ? setMenuOpen((v) => !v) : select(EVERYONE))}
        >
          <span className={styles.peopleLabel}>{personLabel}</span>
          {onPeople && (
            <BiChevronDown className={menuOpen ? styles.chevronOpen : undefined} aria-hidden />
          )}
        </button>
      </div>

      <AnchoredPopover
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorEl={segmentEl}
        label="Assignee"
        align="start"
        width={240}
      >
        <ul className={styles.whoMenu} role="listbox" aria-label="Assignee">
          {options.map((opt) => {
            const active = (person ?? EVERYONE) === opt.value;
            return (
              <li key={opt.value || 'everyone'}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`${styles.whoOption} ${active ? styles.whoOptionActive : ''}`}
                  onClick={() => select(opt.value)}
                >
                  <span className={styles.whoCheck}>{active && <BiCheck />}</span>
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      </AnchoredPopover>
    </div>
  );
};
